/**
 * BrandApplyButton — botón "Mi marca" en la topbar del editor.
 *
 * Aplica al plan actual la identidad de marca del coach (logo, datos, colores)
 * que configuró en "Mi marca". Resuelve dos cosas:
 *   1. Si al cambiar de plantilla / editar se perdió algo de la marca, el coach
 *      la restaura con un clic.
 *   2. Da control fino: aplicar solo el logo, solo los datos, solo los colores
 *      o todo — paridad con el "Aplicar marca" de la app móvil.
 *
 * No toca el contenido del plan (rutina, dieta…), solo el `brand`.
 */
import { useEffect, useState } from "react";
import { Icon } from "../../lib/Icon";
import {
  brandFromUser,
  type CoachBioData,
  type CoverData,
  type PlanBrand,
  type PlanDoc,
} from "../../types/plan";
import type { AetherLiteUser } from "../../api/auth";

interface BrandApplyButtonProps {
  user: AetherLiteUser | null;
  plan: PlanDoc | null;
  /** Patch parcial al `plan.brand` (compat con uso anterior). */
  onApply: (patch: Partial<PlanBrand>) => void;
  /** Aplica una transformación al plan entero — para opciones que tocan
   *  secciones (cover defaults, foto del coach). El BrandApplyButton lo usa
   *  solo cuando hace falta tocar más que `brand`. */
  onApplyToPlan: (mutator: (plan: PlanDoc) => PlanDoc) => void;
  toast: (msg: string) => void;
}

/** Quita claves undefined/null para no pisar el brand del plan con vacíos. */
function clean(patch: Partial<PlanBrand>): Partial<PlanBrand> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out as Partial<PlanBrand>;
}

/** Aplica defaults de portada del coach (eyebrow/subtitle/duration) sobre la
 *  sección cover del plan, si existe. Patch parcial (solo pisa lo que el coach
 *  configuró en Mi marca). */
function applyCoverDefaultsTo(plan: PlanDoc, user: AetherLiteUser): PlanDoc {
  const idx = plan.sections.findIndex((s) => s.type === "cover");
  if (idx < 0) return plan;
  const sec = plan.sections[idx];
  const data = { ...(sec.data as CoverData) };
  if (user.cover_eyebrow) data.eyebrow = user.cover_eyebrow;
  if (user.cover_subtitle) data.programSubtitle = user.cover_subtitle;
  if (user.cover_duration) data.duration = user.cover_duration;
  const sections = plan.sections.slice();
  sections[idx] = { ...sec, data } as typeof sec;
  return { ...plan, sections };
}

/** Setea la foto del coach (de `user.picture_url`) en la sección `coachBio`
 *  si existe. Pisa la foto actual — el coach está pidiendo "aplicar mi marca",
 *  intencionalmente reemplaza. */
function applyCoachPhotoTo(plan: PlanDoc, user: AetherLiteUser): PlanDoc {
  if (!user.picture_url) return plan;
  const idx = plan.sections.findIndex((s) => s.type === "coachBio");
  if (idx < 0) return plan;
  const sec = plan.sections[idx];
  const data: CoachBioData = { ...(sec.data as CoachBioData), photo: user.picture_url };
  const sections = plan.sections.slice();
  sections[idx] = { ...sec, data } as typeof sec;
  return { ...plan, sections };
}

export function BrandApplyButton({ user, plan, onApply, onApplyToPlan, toast }: BrandApplyButtonProps) {
  const [open, setOpen] = useState(false);
  const b = brandFromUser(user);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const apply = (patch: Partial<PlanBrand>, label: string) => {
    onApply(clean(patch));
    toast(`Marca aplicada: ${label}`);
    setOpen(false);
  };

  /** "Todo" → toca brand + cover defaults + foto del coach en una sola pasada. */
  const applyAll = () => {
    if (!b || !user || !plan) return;
    // 1. Patch del brand
    const brandPatch = clean({
      name: b.name, tagline: b.tagline, logoLetters: b.logoLetters, logoUrl: b.logoUrl,
      ig: b.ig, web: b.web, phone: b.phone, watermark: b.watermark,
      primary: b.primary, accent: b.accent,
    });
    // 2. Tocar plan: cover defaults + foto coach (en cadena para evitar 2 saves)
    onApplyToPlan((p) => {
      let next: PlanDoc = { ...p, brand: { ...(p.brand || {}), ...brandPatch } };
      next = applyCoverDefaultsTo(next, user);
      next = applyCoachPhotoTo(next, user);
      return next;
    });
    toast("Marca aplicada: Todo (incluye portada y foto)");
    setOpen(false);
  };

  /** "Datos del entrenador" → brand fields + foto del coach. */
  const applyData = () => {
    if (!b || !user || !plan) return;
    const brandPatch = clean({
      name: b.name, tagline: b.tagline, ig: b.ig, web: b.web, phone: b.phone,
    });
    onApplyToPlan((p) => {
      let next: PlanDoc = { ...p, brand: { ...(p.brand || {}), ...brandPatch } };
      next = applyCoachPhotoTo(next, user);
      return next;
    });
    toast("Marca aplicada: Datos del entrenador (incluye foto)");
    setOpen(false);
  };

  /** "Portada" → eyebrow + subtitle + duration. */
  const applyCover = () => {
    if (!user || !plan) return;
    onApplyToPlan((p) => applyCoverDefaultsTo(p, user));
    toast("Marca aplicada: defaults de portada");
    setOpen(false);
  };

  const options = b
    ? [
        {
          key: "all",
          label: "Todo",
          desc: "Logo, datos, marca de agua, colores, portada y foto",
          run: applyAll,
        },
        {
          key: "logo",
          label: "Logo + marca de agua",
          desc: "Tu logo en la portada y como marca de agua",
          run: () => apply({ logoLetters: b.logoLetters, logoUrl: b.logoUrl, watermark: !!b.logoUrl }, "Logo"),
        },
        {
          key: "data",
          label: "Datos del entrenador",
          desc: "Nombre, tagline, contacto y foto del coach",
          run: applyData,
        },
        {
          key: "cover",
          label: "Defaults de portada",
          desc: "Eyebrow, subtítulo y duración típica",
          run: applyCover,
        },
        {
          key: "colors",
          label: "Colores de marca",
          desc: "Color primario y de acento",
          run: () => apply({ primary: b.primary, accent: b.accent }, "Colores"),
        },
      ]
    : [];

  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen((v) => !v)}
        title="Aplicar tu marca (logo, datos, colores) a este plan"
      >
        <Icon name="star" size={12} /> Mi marca
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
          <div className="al-brand-apply-menu" role="dialog" aria-label="Aplicar mi marca">
            <div className="al-pb-side-eyebrow" style={{ padding: "4px 4px 8px" }}>APLICAR MI MARCA</div>
            {!b ? (
              <div className="tiny muted" style={{ padding: 8 }}>
                Configurá tu marca primero en el menú "Mi marca".
              </div>
            ) : (
              options.map((o) => (
                <button key={o.key} type="button" className="al-brand-apply-opt" onClick={o.run}>
                  <div className="al-brand-apply-opt-label">{o.label}</div>
                  <div className="tiny muted">{o.desc}</div>
                </button>
              ))
            )}
            <div className="tiny muted" style={{ padding: "8px 4px 2px", lineHeight: 1.4 }}>
              No toca el contenido del plan — solo la marca.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
